<template>
  <div class="dashboard-sidebar" :class="{ closed: sidebarClosed }">
    <div class="sidebar">
      <nav v-if="user.id" class="sidebar-wrapper">
        <div class="sidebar-header">
          <a class="sidebar-header-logo"
             href="/">
            <img :src="tenantUrl" class="sidebar-header-logo-image">
          </a>
          <a class="sidebar-header-toggle" href="#" @click="toggleSidebar($event)">
            <i class="demo-icon icon-bars" />
          </a>
        </div>
        <router-link to="/" class="sidebar-home">
          <div class="sidebar-home-wrapper">
            <div class="sidebar-home-wrapper-icon">
              <i class="demo-icon icon-wallet" />
            </div>
            <div class="sidebar-home-wrapper-text">
              <span class="sidebar-home-wrapper-text-header">
                {{ userFullName }}
              </span>
              <span class="sidebar-home-wrapper-text-footer">
                {{ company.id ? company.name : '' }}
              </span>
            </div>
          </div>
        </router-link>

        <ul v-if="setupComplete && !showRevenuesAndReceivables" class="nav sidebar-list">
          <li class="nav-item">
            <router-link to="/invoices/new" class="nav-link sidebar-list-link">
              <i class="demo-icon icon-receipt-new" />
              <span class="sidebar-list-link-text">New Invoice</span>
            </router-link>
          </li>
        </ul>
        <ul v-if="setupComplete" class="nav sidebar-list">
          <li v-if="showRevenuesAndReceivables" class="nav-item d-flex">
            <router-link
              v-if="company.id"
              :to="`/invoices`"
              class="nav-link sidebar-list-link flex-grow-1">
              <i class="demo-icon icon-receipt" />
              <span class="sidebar-list-link-text">Invoices</span>
            </router-link>
            <router-link
              v-if="company.id"
              to="/invoices/new"
              class="nav-link sidebar-list-link sidebar-list-link-create">
              <i class="demo-icon icon-plus" />
            </router-link>
          </li>
          <li class="nav-item d-flex">
            <router-link to="/customers" class="nav-link sidebar-list-link flex-grow-1">
              <i class="demo-icon icon-users" />
              <span class="sidebar-list-link-text">Customers</span>
            </router-link>
            <router-link
              to="/customers/new"
              class="nav-link sidebar-list-link sidebar-list-link-create">
              <i class="demo-icon icon-plus" />
            </router-link>
          </li>
        </ul>
        <ul v-if="userHasInvoices" class="nav sidebar-list">
          <li class="nav-item">
            <router-link
              :to="`/expenses`"
              class="nav-link sidebar-list-link">
              <i class="demo-icon icon-wallet-arrow-up" />
              <span class="sidebar-list-link-text">Expenses</span>
            </router-link>
          </li>
        </ul>
        <ul v-if="setupComplete" class="nav sidebar-list">
          <li class="nav-item">
            <router-link to="/templates" class="nav-link sidebar-list-link">
              <i class="demo-icon icon-receipt-cog" />
              <span class="sidebar-list-link-text">Templates</span>
            </router-link>
          </li>
        </ul>
        <ul v-if="showRevenuesAndReceivables" class="nav sidebar-list">
          <li class="nav-item">
            <router-link to="/payout/new" class="nav-link sidebar-list-link">
              <i class="demo-icon icon-hand-holding-usd" />
              <span class="sidebar-list-link-text">Request Payout</span>
            </router-link>
          </li>
        </ul>
        <ul v-if="showSetup" class="nav sidebar-list">
          <li class="nav-item">
            <a href="#merchantSettingsSubmenu" data-toggle="collapse" aria-expanded="false" class="nav-link sidebar-list-link dropdown-toggle">
              <i class="demo-icon icon-cash-register" />
              <span class="sidebar-list-link-text">Merchant Account</span>
            </a>

            <ul id="merchantSettingsSubmenu" class="list-unstyled sidebar-list-collapse collapse show">
              <li class="nav-item">
                <router-link :to="company.id ? `/merchant/${company.id}/profile` : `/merchant/profile/new`"
                             class="nav-link sidebar-list-setup">
                  <i class="demo-icon" :class="circleClass(1)" />
                  <span class="sidebar-list-setup-text">
                    Create a Merchant Profile
                  </span>
                </router-link>
              </li>
              <li class="nav-item">
                <router-link v-if="setupStageCheck(2)"
                             :to="`/merchant/${company.id}/account`"
                             class="nav-link sidebar-list-setup">
                  <i class="demo-icon" :class="circleClass(2)" />
                  <span class="sidebar-list-setup-text">
                    Create a Merchant Account
                  </span>
                </router-link>
                <span v-else class="sidebar-list-setup sidebar-list-setup-disabled">
                  <i class="demo-icon icon-circle-2" />
                  <span class="sidebar-list-setup-text">
                    Create a Merchant Account
                  </span>
                </span>
              </li>
              <li class="nav-item">
                <router-link v-if="setupStageCheck(3)"
                             to="/templates/new"
                             class="nav-link sidebar-list-setup">
                  <i class="demo-icon" :class="circleClass(3)" />
                  <span class="sidebar-list-setup-text">
                    Create a Template
                  </span>
                </router-link>
                <span v-else class="sidebar-list-setup sidebar-list-setup-disabled">
                  <i class="demo-icon icon-circle-3" />
                  <span class="sidebar-list-setup-text">
                    Create a Template
                  </span>
                </span>
              </li>
            </ul>
          </li>
        </ul>
        <ul v-if="setupComplete" class="nav sidebar-list">
          <li class="nav-item">
            <a href="#merchantSettingsSubmenu" data-toggle="collapse" aria-expanded="false" class="nav-link sidebar-list-link dropdown-toggle collapsed">
              <i class="demo-icon icon-cash-register" />
              <span class="sidebar-list-link-text">Merchant Settings</span>
            </a>

            <ul id="merchantSettingsSubmenu" class="collapse list-unstyled sidebar-list-collapse">
              <li class="nav-item">
                <router-link :to="company.id ? `/merchant/${company.id}/profile` : `/merchant/profile/new`"
                             class="nav-link sidebar-list-link">
                  <i class="demo-icon icon-id" />
                  <span class="sidebar-list-link-text">
                    Merchant Profile
                  </span>
                </router-link>
              </li>
              <li class="nav-item">
                <router-link v-if="company.id"
                             :to="`/merchant/${company.id}/account`"
                             class="nav-link sidebar-list-link">
                  <i class="demo-icon icon-building" />
                  <span class="sidebar-list-link-text">
                    Merchant Account
                  </span>
                </router-link>
              </li>
              <li class="nav-item">
                <router-link v-if="company.id"
                             :to="`/merchant/${company.id}/settings`"
                             class="nav-link sidebar-list-link">
                  <i class="demo-icon icon-cogs" />
                  <span class="sidebar-list-link-text">
                    Settings
                  </span>
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
        <ul class="nav sidebar-list">
          <li class="nav-item">
            <a href="#userSettingsSubmenu" data-toggle="collapse" aria-expanded="false" class="nav-link sidebar-list-link dropdown-toggle collapsed">
              <i class="demo-icon icon-user" />
              <span class="sidebar-list-link-text">User Account</span>
            </a>

            <ul id="userSettingsSubmenu" class="collapse list-unstyled sidebar-list-collapse">
              <li class="nav-item">
                <router-link v-if="user.id"
                             :to="`/user/${user.id}/edit`"
                             class="nav-link sidebar-list-link">
                  <i class="demo-icon icon-id" />
                  <span class="sidebar-list-link-text">User Profile</span>
                </router-link>
              </li>
              <li class="nav-item">
                <router-link v-if="user.id"
                             :to="`/user/${user.id}/password`"
                             class="nav-link sidebar-list-link">
                  <i class="demo-icon icon-lock" />
                  <span class="sidebar-list-link-text">Change Password</span>
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
        <ul class="nav sidebar-list">
          <li class="nav-item">
            <a class="nav-link sidebar-list-link" href="#" @click="logoutUser($event)">
              <i class="demo-icon icon-power-off" />
              <span class="sidebar-list-link-text">Logout</span>
            </a>
          </li>
        </ul>
        <div v-if="!beganSetup" class="nav sidebar-setup">
          <router-link to="/merchant/profile/new"
                       class="btn ihjoz-button sidebar-setup-button"
                       @click.native="beginSetup">
            Create a Merchant Account
          </router-link>
        </div>
      </nav>
      <nav v-else class="sidebar-wrapper">
        <div class="sidebar-header">
          <a class="sidebar-header-logo"
             href="#"
             :style="tenantUrl" />
          <a class="sidebar-header-toggle" href="#" @click="toggleSidebar($event)">
            <i class="demo-icon icon-bars" />
          </a>
        </div>
        <div class="nav sidebar-login">
          <a class="btn ihjoz-button sidebar-login-button"
             href="/login">Login</a>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import CompanyStore from "../../shared/components/Company/CompanyStore";
import SetupStore from "../../shared/components/Setup/SetupStore";
import UserStore from "../../user_dashboard/components/User/UserStore";

export default {
  name: "Sidebar",
  props: {
    sidebarClosed: { type: Boolean },
    tenantUrl: { type: String }
  },

  data () {
    return {
      companyStoreState: CompanyStore.state,
      userStoreState: UserStore.state,
      setupStoreState: SetupStore.state
    };
  },

  computed: {
    company () {
      return this.companyStoreState.company;
    },

    setupStage () {
      return this.setupStoreState.stage;
    },

    beganSetup () {
      return this.setupStoreState.beganSetup;
    },

    showSetup () {
      return (this.setupStage >= 1 && this.setupStage < 4) && this.beganSetup;
    },

    setupComplete () {
      return this.setupStage === 4;
    },

    user () {
      return this.userStoreState.user;
    },

    userFullName () {
      return `${this.user.first_name} ${this.user.last_name}`;
    },

    userHasInvoices () {
      return this.user && this.user.invoices_count;
    },

    companyHasInvoices () {
      return this.company && this.company.invoices_count;
    },

    showInvoiceRoute () {
      return this.$route.name === "Show Invoice";
    },

    showRevenuesAndReceivables () {
      return this.companyHasInvoices || this.showInvoiceRoute;
    }
  },

  methods: {
    async logoutUser (e) {
      e.preventDefault();
      let responseJSON = await UserStore.logout();

      if (responseJSON.success) {
        location.reload();
      }
    },

    toggleSidebar (e) {
      e.preventDefault();
      this.$emit("toggleSidebar");
    },

    beginSetup () {
      SetupStore.beginSetup();
    },

    circleClass (step) {
      if (this.setupStage > step) {
        return "icon-circle-check";
      } else {
        return `icon-circle-${step}`;
      }
    },

    setupStageCheck (step) {
      return this.setupStage >= step;
    }
  }
};
</script>
